import { action, computed, makeObservable, observable } from 'mobx';

export interface AuthState {
  token: string;
  expiresAt: number;
}

/** PRR-316: the tenant admin role — mirrors the backend's AppRoles.Admin ('ADMIN'). Nav visibility is UX only; every endpoint independently enforces this role server-side. */
export const ADMIN_ROLE = 'ADMIN';

/** PRR-356: Payroll Officer and up — mirrors the backend's AppRoles.PayrollOperatorRoles (ADMIN, PAYROLL_MANAGER, PAYROLL_OPERATOR). Presentation only; every payslip endpoint independently enforces these roles server-side. */
export const PAYROLL_OPERATOR_ROLES: readonly string[] = ['ADMIN', 'PAYROLL_MANAGER', 'PAYROLL_OPERATOR'];

class UserStore {
  token: string | null = null;
  expiresAt: number | null = null;
  isInitialising = true;
  roles: string[] = [];
  authEpoch = 0;

  constructor() {
    makeObservable(this, {
      token: observable,
      expiresAt: observable,
      isInitialising: observable,
      roles: observable,
      authEpoch: observable,
      isAuthenticated: computed,
      isAdmin: computed,
      isPayrollOperator: computed,
      setAuth: action,
      clearAuth: action,
      setInitialising: action,
      setRoles: action,
      startNewSession: action,
    });
  }

  get isAuthenticated(): boolean {
    return Boolean(this.token);
  }

  get isAdmin(): boolean {
    return this.roles.includes(ADMIN_ROLE);
  }

  get isPayrollOperator(): boolean {
    return this.roles.some((role) => PAYROLL_OPERATOR_ROLES.includes(role));
  }

  setAuth({ token, expiresAt }: AuthState): void {
    this.token = token;
    this.expiresAt = expiresAt;
  }

  clearAuth(): void {
    this.authEpoch++;
    this.token = null;
    this.expiresAt = null;
    this.roles = [];
  }

  startNewSession(): void {
    this.authEpoch++;
  }

  setInitialising(isInitialising: boolean): void {
    this.isInitialising = isInitialising;
  }

  setRoles(roles: string[]): void {
    this.roles = roles;
  }
}

export const userStore = new UserStore();
